/**
 * Default auth bond setup
 *
 * Uses the built-in createJWTAuthClient from @molecule/app-auth.
 * Swap to a different auth provider by replacing this bond.
 */

import { createJWTAuthClient, type RegisterData, setClient } from '@molecule/app-auth'
import { t } from '@molecule/app-i18n'

import { attachCaptchaToken } from '../components/captcha.js'
import { API_BASE_URL, authConfig } from '../config.js'
import { emitNotification } from '../notifications.js'
import { hasGuestCookie, stashGuestUserId } from '../utilities/guestClaim.js'
import { notifySessionAdopted } from '../utilities/session-events.js'
import { clearGuestMarker, markExplicitLogout } from './app-http.js'

export const authClient = createJWTAuthClient(authConfig)

/** After login/register, claim any guest projects from the anonymous session. */
export async function claimGuestProjects(): Promise<void> {
  const guestUserId = sessionStorage.getItem('mol_guest_user_id')
  if (!guestUserId) return
  sessionStorage.removeItem('mol_guest_user_id')

  try {
    const response = await fetch(`${API_BASE_URL}/guest/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      // Attach the human-verification token (P2-26) so the server's
      // requireCaptcha gate can confirm the claim is human-originated. A no-op
      // when captcha is unconfigured (dev/local) or no challenge was solved.
      body: JSON.stringify(attachCaptchaToken({ guestUserId })),
    })
    // The server clears the guest cookies on a successful claim. Also drop the
    // JS-readable uid hint locally when the guest is UNCLAIMABLE (already
    // claimed/expired → 404, or our secret doesn't own it → 403): a stale hint
    // would keep guest-gating the UI (AuthGuard's allowGuest, guest CTAs) for a
    // browser that has no live guest. Left in place on 5xx/network errors so a
    // transient failure doesn't forfeit a real claim hint.
    if (response.status === 503) {
      // The service was busy (no database connection for the quota section):
      // nothing moved. Keep the hint so the dashboard retries, and say so.
      sessionStorage.setItem('mol_guest_user_id', guestUserId)
      const busy = (await response.json().catch(() => null)) as { error?: unknown } | null
      emitNotification(
        (typeof busy?.error === 'string' && busy.error) ||
          t('service.busy', undefined, {
            defaultValue: 'The service is busy. Try again in a moment.',
          }),
        'warning',
      )
      return
    }
    // Anything else that is not a success and not an unclaimable guest (a 500, a 429, a 401, a
    // proxy error page): nothing is known to have moved, and the hint was dropped above. Put it
    // back so the next authenticated visit retries, and say so. Without this the guest's project
    // was silently abandoned and swept after 72 hours (R144-M1).
    if (!response.ok && response.status !== 403 && response.status !== 404) {
      sessionStorage.setItem('mol_guest_user_id', guestUserId)
      emitNotification(claimRetryMessage(), 'warning')
      return
    }
    const body = response.ok
      ? ((await response.json().catch(() => null)) as {
          left?: unknown
          message?: unknown
        } | null)
      : null
    const left = typeof body?.left === 'number' ? body.left : 0
    // A capped claim (left > 0) keeps the hint cookie and the guest marker: the
    // server keeps the guest secret so a later sign-in from this browser can
    // move the leftovers. Only a full claim or an unclaimable guest clears them.
    if ((response.ok && left === 0) || response.status === 403 || response.status === 404) {
      document.cookie = 'mol_guest_uid=; Max-Age=0; path=/'
      clearGuestMarker()
      // An unclaimable guest ends the pending-claim bookkeeping too, so a later
      // guest in this tab is not compared against a stale outcome.
      if (!response.ok) sessionStorage.removeItem('mol_guest_claim_left')
    }
    if (response.ok) {
      if (left === 0) sessionStorage.removeItem('mol_guest_claim_left')
      if (left > 0) {
        // The plan cap left projects on the guest (the server keeps the guest
        // secret for exactly this): remember the hint so the claim runs again
        // on the next authenticated visit — after an upgrade, or once a project
        // was deleted — and say what happened, with the way out.
        sessionStorage.setItem('mol_guest_user_id', guestUserId)
        // Say it once per outcome: a retry that left the same projects behind
        // adds no new information.
        const lastLeft = sessionStorage.getItem('mol_guest_claim_left')
        sessionStorage.setItem('mol_guest_claim_left', String(left))
        if (lastLeft === String(left)) return
        emitNotification(
          (typeof body?.message === 'string' && body.message) ||
            t(
              'guest.claim.projectLimit',
              { left },
              {
                defaultValue:
                  'Your plan is full, so {{left}} of your guest projects stayed behind. They are deleted in a few days unless you upgrade or delete a project to make room.',
              },
            ),
          'warning',
          {
            label: t('upgrade.viewPlans', undefined, { defaultValue: 'View plans' }),
            href: '/pricing',
          },
        )
      }
    }
  } catch (_error) {
    // A network failure must not block the auth flow, but it must not forfeit the claim either:
    // keep the hint so the next authenticated visit retries.
    sessionStorage.setItem('mol_guest_user_id', guestUserId)
    emitNotification(claimRetryMessage(), 'warning')
  }
}

/** What the user is told when moving their guest project did not work yet. */
function claimRetryMessage(): string {
  return t('guest.claim.retrying', undefined, {
    defaultValue:
      'We could not move your project to your account yet. We will try again when you next open the dashboard.',
  })
}

/**
 * Re-run a claim the plan cap cut short (see above) on an authenticated page
 * load — the pending hint is only set when projects were left behind. No-op
 * otherwise.
 */
export async function retryPendingGuestClaim(): Promise<void> {
  // The guest cookie is the durable record of a claim that has not happened: the server clears it
  // on success, so one still present on an authenticated page means a project is waiting.
  if (!sessionStorage.getItem('mol_guest_user_id') && hasGuestCookie()) stashGuestUserId()
  if (!sessionStorage.getItem('mol_guest_user_id')) return
  // At most one retry per ten minutes per tab: a user who stays at the cap
  // must not re-POST the claim on every dashboard visit.
  const last = Number(sessionStorage.getItem('mol_guest_claim_retried_at') ?? 0)
  if (Date.now() - last < 10 * 60_000) return
  sessionStorage.setItem('mol_guest_claim_retried_at', String(Date.now()))
  await claimGuestProjects()
}

/**
 * Adopt a freshly-authenticated session on a page that KEEPS RUNNING across the
 * login (the in-IDE auth modal: password, signup, and popup OAuth — none of
 * them navigate). The http client may still hold the PREVIOUS session's bearer
 * (a guest's short-lived WS ticket, primed for cookie-only guests — or an
 * expired user's). The server prefers the Authorization header over the fresh
 * login cookie, and the guest claim below deletes the guest user + its devices,
 * so keeping that bearer would 401 ("Unauthorized") every subsequent request —
 * the logged-in user never takes over the session.
 *
 * Order matters: drop the stale bearer FIRST so the claim and the ticket mint
 * ride the new httpOnly session cookie, then re-mint a WS ticket for the REAL
 * session so cross-origin WebSockets keep authenticating.
 *
 * @param http - The app's bonded http client (`useHttpClient()`).
 */
export async function adoptLoginSession(http: {
  setAuthToken(token: string | null): void
  get<T>(url: string): Promise<{ data: T }>
}): Promise<void> {
  http.setAuthToken(null)
  clearGuestMarker()
  await claimGuestProjects()
  try {
    const res = await http.get<{ token?: string }>('/auth/ws-token')
    if (res.data?.token) http.setAuthToken(res.data.token)
  } catch (_error) {
    // Same-origin requests still authenticate via the httpOnly cookie; only
    // the cross-origin WS ticket is degraded until the next full page load.
  }
  // The claim above may have reassigned the guest's projects to the new user;
  // the tab never reloaded, so tell any mounted view to re-fetch its user-scoped
  // data (e.g. the Dashboard project list) instead of showing stale guest state.
  notifySessionAdopted()
}

/** Guard so the register wrapper is installed exactly once across setup calls. */
let captchaRegisterWired = false
/** Guard so the logout wrapper is installed exactly once. */
let logoutWired = false

export function setupAuthDefault(): void {
  // Attach the human-verification token (P2-26) to signup so the API's
  // requireCaptcha gate can confirm a new free account is human-originated. The
  // token is injected from the shared captcha store at call time — a no-op when
  // captcha is unconfigured (dev/local) — so signup stays decoupled from the
  // widget. Wrapped here, at the single bond-wiring entry point, so EVERY
  // register() path (the signup form, any future caller) inherits it rather
  // than each form re-implementing token plumbing. Guarded against the mocked
  // client (no `register`) and against double-wiring.
  if (!captchaRegisterWired && typeof authClient.register === 'function') {
    const baseRegister = authClient.register.bind(authClient)
    authClient.register = (data: RegisterData) => baseRegister(attachCaptchaToken(data))
    captchaRegisterWired = true
  }
  // An explicit logout ends any guest identity this browser remembered.
  if (!logoutWired && typeof authClient.logout === 'function') {
    const baseLogout = authClient.logout.bind(authClient)
    authClient.logout = async () => {
      // Mark FIRST: the 401 interceptor must recognize every response this
      // sign-out produces as belonging to the explicit flow, so the caller's
      // single navigation is the only one.
      markExplicitLogout()
      clearGuestMarker()
      return baseLogout()
    }
    logoutWired = true
  }
  setClient(authClient)
}
